import { fileUrlWithParam } from "@/lib/api";
import { useActivityTracker } from "@/hooks/useActivityTracker";
import { useRef } from "react";
import { AppLogo } from "@/components/AppLogo";

/**
 * Lightweight image used across the catalog. Builds a stable URL into the
 * public /api/files route — all images are byte-identical for every visitor
 * (now that watermarking is removed), so browsers + the CDN share one cached
 * copy across the whole audience instead of one per token.
 *
 * Pass `thumb` to request the small (~600px) preview variant — perfect for grid
 * tiles. The lightbox / detail pages should omit `thumb` to load the full-resolution
 * image for pinch-zoom.
 *
 * Right-click and long-press attempts are still reported to /api/track so the
 * admin dashboard can flag suspicious traders. Pass
 * `trackContext={{ cardId, seriesId }}` for richer alerts.
 */
export default function AuthedImage({
  path,
  alt = "",
  className = "",
  placeholderClassName = "",
  thumb = false,
  eager = false,
  draggable,
  style,
  onContextMenu,
  onClick,
  trackContext,
  ...rest
}) {
  const track = useActivityTracker();
  const pressTimer = useRef(null);

  if (!path) {
    return (
      <div className={`flex items-center justify-center bg-[#F5F2EA] ${placeholderClassName || className}`}>
        <AppLogo size="lg" className="opacity-60" />
      </div>
    );
  }
  const src = fileUrlWithParam(path, thumb ? { thumb: 1 } : {});

  const ctx = trackContext || {};

  const handleContextMenu = (e) => {
    track("RIGHT_CLICK", { card_id: ctx.cardId, series_id: ctx.seriesId });
    if (onContextMenu) onContextMenu(e);
  };

  const handleTouchStart = () => {
    if (pressTimer.current) clearTimeout(pressTimer.current);
    pressTimer.current = setTimeout(() => {
      track("LONG_PRESS", { card_id: ctx.cardId, series_id: ctx.seriesId });
    }, 600);
  };
  const handleTouchEnd = () => {
    if (pressTimer.current) {
      clearTimeout(pressTimer.current);
      pressTimer.current = null;
    }
  };

  return (
    <img
      src={src}
      alt={alt}
      className={className}
      style={style}
      draggable={draggable}
      loading={eager ? "eager" : "lazy"}
      // fetchpriority="high" tells the browser to prioritise these images in the
      // network queue ahead of fonts/below-the-fold images. Above-the-fold tiles
      // become visually complete much faster on slow mobile networks.
      fetchpriority={eager ? "high" : "auto"}
      decoding="async"
      onContextMenu={handleContextMenu}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchEnd}
      onClick={onClick}
      onError={(e) => { e.currentTarget.style.display = "none"; }}
      {...rest}
    />
  );
}
